import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';

// Helpers
import { numberAsDollar } from '../helpers/formatters';

// Types
import { ISalaryRangeMeta } from '../types';

const dollarFormatOptions = { minimumFractionDigits: 2, notation: 'compact' } as Intl.NumberFormatOptions;
// const dollarFormatOptions = { minimumFractionDigits: 0, notation: 'compact' } as Intl.NumberFormatOptions;

export const useRangeLabel = () => {
    const { t } = useTranslation();
    const getRangeLabel = React.useCallback(
        (
            meta: ISalaryRangeMeta,
            asString?: Boolean
        ): {
            sectionLabel: string | React.ReactNode;
            totalFieldLabel: string | React.ReactNode;
        } => {
            if (typeof meta.hour?.high !== 'undefined' && meta.hour?.low === 0) {
                return {
                    sectionLabel: asString ? (
                        t('tierSectionUnder.label', {
                            ns: 'questions',
                            hourly: numberAsDollar(meta.hour.high, dollarFormatOptions)
                        })
                    ) : (
                        <Trans
                            t={t}
                            i18nKey={'tierSectionUnder.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourly: numberAsDollar(meta.hour?.high + 0.01, dollarFormatOptions)
                            }}
                        ></Trans>
                    ),
                    totalFieldLabel: (
                        <Trans
                            t={t}
                            i18nKey={'employeeCountWageTierUnder.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourly: numberAsDollar(meta.hour?.high, dollarFormatOptions)
                            }}
                        ></Trans>
                    )
                };
            } else if (
                typeof meta.annual?.high === 'undefined' &&
                typeof meta.annual?.low !== 'undefined' &&
                typeof meta.hour?.low !== 'undefined'
            ) {
                return {
                    sectionLabel: asString ? (
                        t('tierSectionOver.label', {
                            ns: 'questions',
                            hourly: numberAsDollar(meta.hour.low, dollarFormatOptions),
                            annual: numberAsDollar(meta.annual.low, dollarFormatOptions)
                        })
                    ) : (
                        <Trans
                            t={t}
                            i18nKey={'tierSectionOver.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourly: numberAsDollar(meta.hour?.low, dollarFormatOptions),
                                annual: numberAsDollar(meta.annual?.low, dollarFormatOptions)
                            }}
                        ></Trans>
                    ),
                    totalFieldLabel: (
                        <Trans
                            t={t}
                            i18nKey={'employeeCountWageTierOver.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourly: numberAsDollar(meta.hour?.low, dollarFormatOptions),
                                annual: numberAsDollar(meta.annual?.low, dollarFormatOptions)
                            }}
                        ></Trans>
                    )
                };
            } else if (
                typeof meta.annual?.low !== 'undefined' &&
                typeof meta.annual?.high !== 'undefined' &&
                typeof meta.hour?.low !== 'undefined' &&
                typeof meta.hour?.high !== 'undefined'
            ) {
                return {
                    sectionLabel: asString ? (
                        t('tierSectionBetween.label', {
                            ns: 'questions',
                            hourlyLow: numberAsDollar(meta.hour.low, dollarFormatOptions),
                            hourlyHigh: numberAsDollar(meta.hour.high, dollarFormatOptions),
                            annuallyLow: numberAsDollar(meta.annual.low, dollarFormatOptions),
                            annuallyHigh: numberAsDollar(meta.annual.high, dollarFormatOptions)
                        })
                    ) : (
                        <Trans
                            t={t}
                            i18nKey={'tierSectionBetween.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourlyLow: numberAsDollar(meta.hour?.low, dollarFormatOptions),
                                hourlyHigh: numberAsDollar(meta.hour?.high, dollarFormatOptions),
                                annuallyLow: numberAsDollar(meta.annual?.low, dollarFormatOptions),
                                annuallyHigh: numberAsDollar(meta.annual?.high, dollarFormatOptions)
                            }}
                        ></Trans>
                    ),
                    totalFieldLabel: (
                        <Trans
                            t={t}
                            i18nKey={'employeeCountWageTierBetween.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourlyLow: numberAsDollar(meta.hour?.low, dollarFormatOptions),
                                hourlyHigh: numberAsDollar(meta.hour?.high, dollarFormatOptions),
                                annuallyLow: numberAsDollar(meta.annual?.low, dollarFormatOptions),
                                annuallyHigh: numberAsDollar(meta.annual?.high, dollarFormatOptions)
                            }}
                        ></Trans>
                    )
                };
            } else if (
                !meta.annual &&
                typeof meta.hour?.low !== 'undefined' &&
                typeof meta.hour?.high !== 'undefined'
            ) {
                return {
                    sectionLabel: asString ? (
                        t('tierSectionBetweenHourOnly.label', {
                            ns: 'questions',
                            hourlyLow: numberAsDollar(meta.hour.low, dollarFormatOptions),
                            hourlyHigh: numberAsDollar(meta.hour.high, dollarFormatOptions)
                        })
                    ) : (
                        <Trans
                            t={t}
                            i18nKey={'tierSectionBetweenHourOnly.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourlyLow: numberAsDollar(meta.hour?.low, dollarFormatOptions),
                                hourlyHigh: numberAsDollar(meta.hour?.high, dollarFormatOptions)
                            }}
                        ></Trans>
                    ),
                    totalFieldLabel: null
                };
            } else if (
                !meta.annual &&
                typeof meta.hour?.low !== 'undefined' &&
                typeof meta.hour?.high === 'undefined'
            ) {
                return {
                    sectionLabel: asString ? (
                        t('tierSectionOverHourOnly.label', {
                            ns: 'questions',
                            hourly: numberAsDollar(meta.hour?.low, dollarFormatOptions)
                        })
                    ) : (
                        <Trans
                            t={t}
                            i18nKey={'tierSectionOverHourOnly.label'}
                            ns="questions"
                            components={{
                                strong: <strong />
                            }}
                            values={{
                                hourly: numberAsDollar(meta.hour?.low, dollarFormatOptions)
                            }}
                        ></Trans>
                    ),
                    totalFieldLabel: null
                };
            }

            return {
                sectionLabel: '',
                totalFieldLabel: ''
            };
        },
        [t]
    );

    return { getRangeLabel };
};
